import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Radio, Heart, SkipForward } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

/**
 * RadioScreen — the interactive Radio phone mockup on the homepage.
 *
 * Fully wired to PlayerContext so the phone mirrors real playback:
 *   • When the radio is playing, the phone shows the CURRENT track + cover art.
 *   • When idle, it shows the top-1 trending track as an ambient preview.
 *   • Tapping Play (or the whole vinyl) starts the round-robin radio queue.
 *   • Tapping Skip advances to the next track.
 *
 * The waveform only animates when playback is actually running so the
 * mockup honestly reflects "on-air" state.
 */
export default function RadioScreen() {
  const { current, playing, playRadio, toggle, next, mode } = usePlayer();
  const [top, setTop] = useState([]);
  const isRadio = mode === "radio";
  // Show the live track if the user is on the radio, otherwise preview the top-1.
  const display = isRadio && current ? current : top[0] || null;

  useEffect(() => {
    api.get("/radio/top?limit=30")
      .then((r) => setTop(r.data.tracks || []))
      .catch(() => setTop([]));
  }, []);

  const handlePlay = (e) => {
    // The vinyl area and the button both call this — stop the phone tilt
    // from swallowing the click and prevent default touch bubbling.
    e?.stopPropagation?.();
    if (isRadio && current) {
      toggle();
      return;
    }
    if (top[0]) playRadio(top[0], top);
  };

  const handleSkip = (e) => {
    e?.stopPropagation?.();
    if (isRadio) next();
    else if (top[1]) playRadio(top[1], top);
  };

  const cover = display?.cover_path ? fileUrl(display.cover_path) : "";
  const showAnimated = playing && isRadio;

  return (
    <div className="relative h-full w-full bg-gradient-to-b from-[#111] via-[#0A0A0A] to-black text-white p-4 flex flex-col">
      {/* Status bar */}
      <div className="flex items-center justify-between font-mono text-[9px] text-gray-400 mb-4">
        <span>9:41</span>
        <span className={`uppercase tracking-widest ${showAnimated ? "text-[#FF4500]" : "text-gray-500"}`}>
          {showAnimated ? "LIVE · Radio" : "MeloCraft · Radio"}
        </span>
      </div>

      {/* Vinyl / cover art */}
      <button
        type="button"
        data-testid="hero-radio-play-vinyl"
        onClick={handlePlay}
        aria-label={showAnimated ? "Pause radio" : "Play radio"}
        className="relative mx-auto my-4 h-40 w-40 group focus:outline-none"
      >
        <motion.div
          animate={showAnimated ? { rotate: 360 } : { rotate: 0 }}
          transition={showAnimated
            ? { duration: 8, repeat: Infinity, ease: "linear" }
            : { duration: 0.4 }}
          className="h-40 w-40 rounded-full border border-white/10 shadow-2xl overflow-hidden"
          style={{
            backgroundImage: cover
              ? `radial-gradient(circle at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 26%, rgba(0,0,0,0.85) 27%, rgba(0,0,0,0) 28%), url(${cover})`
              : "repeating-radial-gradient(circle at center, rgba(255,255,255,0.03) 0 1px, transparent 1px 3px), radial-gradient(circle at center, #FF4500 0%, #1a1a1a 18%, #0a0a0a 60%, #000 100%)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 grid place-items-center">
            <div className="h-4 w-4 rounded-full bg-black border border-white/20" />
          </div>
        </motion.div>

        {/* Hover / idle overlay play glyph */}
        <div className={`absolute inset-0 grid place-items-center transition-opacity ${showAnimated ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
          <div className={`h-12 w-12 rounded-full grid place-items-center transition-colors ${showAnimated ? "bg-black/60" : "bg-[#FF4500]"} shadow-[0_0_30px_-5px_rgba(255,69,0,0.9)]`}>
            {showAnimated ? (
              <Pause className="h-5 w-5 text-white" fill="currentColor" />
            ) : (
              <Play className="h-5 w-5 text-black translate-x-[1px]" fill="currentColor" />
            )}
          </div>
        </div>
      </button>

      {/* Track meta */}
      <div className="text-center px-2 min-h-[42px]">
        <div className="font-mono text-[9px] uppercase tracking-widest text-[#FF4500] mb-1 flex items-center justify-center gap-1">
          <Radio className="h-2.5 w-2.5" />
          {showAnimated ? "Now streaming" : "Tap to tune in"}
        </div>
        <div className="font-display font-bold text-base leading-tight truncate">
          {display?.title || "MeloCraft Radio"}
        </div>
        <div className="font-mono text-[10px] text-gray-400 truncate">
          {display?.artist_name || "Every artist. Free forever."}
        </div>
      </div>

      {/* Waveform */}
      <div className="flex items-end justify-center gap-[3px] h-10 mt-4">
        {Array.from({ length: 22 }).map((_, i) => (
          <motion.span
            key={i}
            className="w-[3px] bg-[#FF4500] origin-bottom rounded-sm"
            animate={showAnimated
              ? { scaleY: [0.3, Math.random() * 0.9 + 0.3, 0.3] }
              : { scaleY: 0.25 }}
            transition={showAnimated
              ? { duration: 0.6 + Math.random() * 0.6, repeat: Infinity, delay: i * 0.05, ease: "easeInOut" }
              : { duration: 0.3 }}
            style={{ height: "100%" }}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center justify-around">
        <Heart className="h-4 w-4 text-gray-500" />
        <button
          type="button"
          data-testid="hero-radio-play-btn"
          onClick={handlePlay}
          aria-label={showAnimated ? "Pause radio" : "Play radio"}
          className="h-12 w-12 rounded-full bg-[#FF4500] grid place-items-center shadow-[0_0_30px_-5px_rgba(255,69,0,0.9)] hover:bg-[#E53E00] transition-colors"
        >
          {showAnimated
            ? <Pause className="h-5 w-5 text-black" fill="currentColor" />
            : <Play className="h-5 w-5 text-black translate-x-[1px]" fill="currentColor" />}
        </button>
        <button
          type="button"
          data-testid="hero-radio-skip-btn"
          onClick={handleSkip}
          aria-label="Skip track"
          className="text-gray-400 hover:text-white transition-colors"
        >
          <SkipForward className="h-4 w-4" />
        </button>
      </div>

      {/* Bottom home indicator */}
      <div className="mt-auto pt-3 flex justify-center">
        <div className="h-1 w-24 rounded-full bg-white/30" />
      </div>
    </div>
  );
}
